import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { authAPI } from "../../services/api";
import { ShieldCheck, ShieldOff, Copy, Check } from "lucide-react";
import toast from "react-hot-toast";

const inputClass =
  "w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/70 focus:outline-none";

const STEPS = {
  IDLE: "idle",
  SCANNING: "scanning",
  VERIFYING: "verifying",
  BACKUP_CODES: "backup_codes",
  DISABLING: "disabling",
};

const TwoFactorSetup = ({ enabled, onStatusChange }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(STEPS.IDLE);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  const {
    register: registerEnable,
    handleSubmit: handleEnableSubmit,
    formState: { errors: enableErrors, isSubmitting: isEnableSubmitting },
    reset: resetEnable,
    setError: setEnableError,
  } = useForm();

  const {
    register: registerDisable,
    handleSubmit: handleDisableSubmit,
    formState: { errors: disableErrors, isSubmitting: isDisableSubmitting },
    reset: resetDisable,
    setError: setDisableError,
  } = useForm();

  const startSetup = async () => {
    try {
      const data = await authAPI.twoFactorSetup();
      setQrDataUrl(data.qrDataUrl);
      setStep(STEPS.SCANNING);
    } catch {
      toast.error(t("twoFactor.setup.setupError"));
    }
  };

  const onEnableSubmit = async ({ code }) => {
    try {
      const data = await authAPI.twoFactorEnable(code);
      setBackupCodes(data.backupCodes);
      resetEnable();
      setStep(STEPS.BACKUP_CODES);
      onStatusChange(true);
      toast.success(t("twoFactor.setup.enabledSuccess"));
    } catch (error) {
      const msg =
        error.response?.data?.error || t("twoFactor.setup.invalidCode");
      setEnableError("code", { message: msg });
    }
  };

  const onDisableSubmit = async ({ password }) => {
    try {
      await authAPI.twoFactorDisable(password);
      resetDisable();
      setStep(STEPS.IDLE);
      onStatusChange(false);
      toast.success(t("twoFactor.setup.disabledSuccess"));
    } catch (error) {
      const msg =
        error.response?.data?.error || t("twoFactor.setup.disableError");
      setDisableError("password", { message: msg });
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const finishSetup = () => {
    setStep(STEPS.IDLE);
    setQrDataUrl(null);
    setBackupCodes([]);
  };

  // ─── BACKUP CODES display ─────────────────────────────────────────────────
  if (step === STEPS.BACKUP_CODES) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-sm font-semibold text-amber-200">
            {t("twoFactor.setup.backupCodesTitle")}
          </p>
          <p className="mt-1 text-xs text-amber-200/70">
            {t("twoFactor.setup.backupCodesHint")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {backupCodes.map((c) => (
            <span
              key={c}
              className="rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2 text-center font-mono text-sm text-white"
            >
              {c}
            </span>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={copyBackupCodes}
            className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/90 transition hover:bg-white/10"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-400" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied
              ? t("twoFactor.setup.copied")
              : t("twoFactor.setup.copyBackupCodes")}
          </button>
          <button
            type="button"
            onClick={finishSetup}
            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30"
          >
            {t("twoFactor.setup.done")}
          </button>
        </div>
      </div>
    );
  }

  // ─── SCANNING step: show QR code ──────────────────────────────────────────
  if (step === STEPS.SCANNING) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          {t("twoFactor.setup.scanInstructions")}
        </p>
        {qrDataUrl && (
          <div className="flex justify-center">
            <img
              src={qrDataUrl}
              alt="TOTP QR code"
              className="h-48 w-48 rounded-2xl border border-white/10 bg-white p-2"
            />
          </div>
        )}
        <p className="text-sm text-slate-300">
          {t("twoFactor.setup.enterCodeInstructions")}
        </p>
        <form
          onSubmit={handleEnableSubmit(onEnableSubmit)}
          className="space-y-3"
        >
          <div>
            <input
              {...registerEnable("code", {
                required: t("twoFactor.setup.codeRequired"),
                pattern: {
                  value: /^\d{6}$/,
                  message: t("twoFactor.setup.invalidCode"),
                },
              })}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className={`${inputClass} text-center tracking-[0.4em] text-lg`}
              placeholder="------"
            />
            {enableErrors.code && (
              <p className="mt-1 text-xs text-rose-300">
                {enableErrors.code.message}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setStep(STEPS.IDLE);
                setQrDataUrl(null);
                resetEnable();
              }}
              className="rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/90 transition hover:bg-white/10"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={isEnableSubmitting}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30 disabled:opacity-60"
            >
              {isEnableSubmitting ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                t("twoFactor.setup.verifyAndEnable")
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── DISABLING step ───────────────────────────────────────────────────────
  if (step === STEPS.DISABLING) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          {t("twoFactor.setup.disableConfirm")}
        </p>
        <form
          onSubmit={handleDisableSubmit(onDisableSubmit)}
          className="space-y-3"
        >
          <div>
            <input
              {...registerDisable("password", {
                required: t("twoFactor.setup.passwordRequired"),
              })}
              type="password"
              autoComplete="current-password"
              className={inputClass}
              placeholder={t("twoFactor.setup.passwordPlaceholder")}
            />
            {disableErrors.password && (
              <p className="mt-1 text-xs text-rose-300">
                {disableErrors.password.message}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setStep(STEPS.IDLE);
                resetDisable();
              }}
              className="rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/90 transition hover:bg-white/10"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={isDisableSubmitting}
              className="inline-flex items-center gap-2 rounded-2xl border border-red-500/40 bg-red-500/10 px-5 py-2 text-sm font-semibold text-red-300 transition hover:bg-red-500/20 disabled:opacity-60"
            >
              {isDisableSubmitting ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-300/30 border-t-red-300" />
              ) : (
                t("twoFactor.setup.confirmDisable")
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── IDLE state ───────────────────────────────────────────────────────────
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {enabled ? (
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
        ) : (
          <ShieldOff className="h-5 w-5 text-slate-400" />
        )}
        <div>
          <p className="text-sm font-medium text-white">
            {enabled
              ? t("twoFactor.setup.enabledLabel")
              : t("twoFactor.setup.disabledLabel")}
          </p>
          <p className="text-xs text-slate-400">
            {enabled
              ? t("twoFactor.setup.enabledDescription")
              : t("twoFactor.setup.disabledDescription")}
          </p>
        </div>
      </div>
      {enabled ? (
        <button
          type="button"
          onClick={() => setStep(STEPS.DISABLING)}
          className="shrink-0 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300 transition hover:bg-red-500/20"
        >
          {t("twoFactor.setup.disable")}
        </button>
      ) : (
        <button
          type="button"
          onClick={startSetup}
          className="shrink-0 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30"
        >
          {t("twoFactor.setup.enable")}
        </button>
      )}
    </div>
  );
};

export default TwoFactorSetup;
